/**
 * Pricing Calculations Utility
 * Handles tier detection and UG (Unités Gratuites) calculations
 */

export interface PricingTier {
    tier_number: number;
    min_quantity: number;
    max_quantity?: number | null;
    discount_percentage?: number | null;
    free_units?: number | null;
}

export interface UGCalculationResult {
    ordered_units: number;
    free_units: number;
    total_received: number;
    unit_price: number;
    total_cost: number;
    display_text: string;
    tier_triggered: boolean;
    tier_number?: number;
}

/**
 * Detect which tier applies based on quantity ordered
 * @param quantity - Total units ordered
 * @param tiers - Array of pricing tiers
 * @returns The applicable tier or null
 */
export function detectTier(quantity: number, tiers: PricingTier[]): PricingTier | null {
    if (!tiers || tiers.length === 0) return null;

    // Select the tier with the highest min_quantity reached (min only)
    const sortedTiers = [...tiers].sort((a, b) => b.min_quantity - a.min_quantity);
    return sortedTiers.find(tier => quantity >= tier.min_quantity) ?? null;
}

/**
 * Calculate pricing with UG (Unités Gratuites - Free Units)
 * Formula: Prix Client CHT = Prix Catalogue CHT × Ordered Units / Total Received
 * 
 * @param orderedUnits - Units being ordered (and paid for)
 * @param basePrice - Base catalog price per unit
 * @param freeUnits - Free units given
 * @returns Calculation result with adjusted pricing
 */
export function calculateUGPrice(
    orderedUnits: number,
    basePrice: number,
    freeUnits: number,
    orderedLots: number
): UGCalculationResult {
    const totalReceived = orderedUnits + freeUnits;

    // Calculate adjusted unit price: distribute cost over all received units
    const adjustedUnitPrice = basePrice * (orderedUnits / totalReceived);

    // Total cost: pay for ordered units at adjusted price
    const totalCost = orderedUnits * adjustedUnitPrice;

    return {
        ordered_units: orderedUnits,
        free_units: freeUnits,
        total_received: totalReceived,
        unit_price: adjustedUnitPrice,
        total_cost: totalCost,
        display_text: `${orderedLots} lot${orderedLots > 1 ? 's' : ''} + ${freeUnits} gratuit`,
        tier_triggered: freeUnits > 0,
        tier_number: undefined
    };
}

/**
 * Calculate pricing with percentage discount
 * @param orderedUnits - Units being ordered
 * @param basePrice - Base catalog price per unit
 * @param discountPercentage - Discount percentage (0-100)
 * @returns Calculation result
 */
export function calculatePercentagePrice(
    orderedUnits: number,
    basePrice: number,
    discountPercentage: number,
    orderedLots: number
): UGCalculationResult {
    const adjustedUnitPrice = basePrice * (1 - discountPercentage / 100);
    const totalCost = orderedUnits * adjustedUnitPrice;

    return {
        ordered_units: orderedUnits,
        free_units: 0,
        total_received: orderedUnits,
        unit_price: adjustedUnitPrice,
        total_cost: totalCost,
        display_text: `${orderedLots} lot${orderedLots > 1 ? 's' : ''}`,
        tier_triggered: discountPercentage > 0,
        tier_number: undefined
    };
}

/**
 * Calculate pricing based on tier (handles both UG and percentage modes)
 * @param orderedUnits - Units being ordered
 * @param basePrice - Base catalog price per unit
 * @param tiers - Array of pricing tiers
 * @returns Calculation result with tier information
 */
export function calculatePricingWithTiers(
    orderedLots: number,
    basePrice: number,
    tiers: PricingTier[],
    batchSize: number
): UGCalculationResult {
    const orderedUnits = orderedLots * batchSize;
    const tier = detectTier(orderedLots, tiers);

    if (!tier) {
        // No tier applies - use base price
        return {
            ordered_units: orderedUnits,
            free_units: 0,
            total_received: orderedUnits,
            unit_price: basePrice,
            total_cost: orderedUnits * basePrice,
            display_text: `${orderedLots} lot${orderedLots > 1 ? 's' : ''}`,
            tier_triggered: false
        };
    }

    let result: UGCalculationResult;

    // Check if tier has free_units (UG mode) or discount_percentage (% mode)
    if (tier.free_units !== null && tier.free_units !== undefined && tier.free_units > 0) {
        // UG Mode
        result = calculateUGPrice(orderedUnits, basePrice, tier.free_units, orderedLots);
    } else if (tier.discount_percentage !== null && tier.discount_percentage !== undefined) {
        // Percentage Mode
        result = calculatePercentagePrice(orderedUnits, basePrice, tier.discount_percentage, orderedLots);
    } else {
        // Tier exists but no discount - use base price
        result = {
            ordered_units: orderedUnits,
            free_units: 0,
            total_received: orderedUnits,
            unit_price: basePrice,
            total_cost: orderedUnits * basePrice,
            display_text: `${orderedLots} lot${orderedLots > 1 ? 's' : ''}`,
            tier_triggered: false
        };
    }

    // Add tier information
    result.tier_number = tier.tier_number;

    return result;
}
